import React, { useState, useEffect, useCallback, useRef } from "react";
import '../../css/index.css';
import HttpService from "../../services/HttpService";
import DependencyGroup from "./Components/DependencyGroup";
import Button from "../Tools/Components/Button";
import CreateDependencyGroupForm from "./Components/CreateDependencyGroupForm";
import { useTranslation } from 'react-i18next';

const ValidatorOverzichtPagina = () => {
	const [dependencyGroups, setDependencyGroups] = useState([]);
	const [searchString, setSearchString] = useState("");
	const [editing, setEditing] = useState(false);
	const [showCreateGroupForm, setShowCreateGroupForm] = useState(false);
	const [selectedGroupName, setSelectedGroupName] = useState("");
	const [createOrUpdateDependencyGroupRequest, setCreateOrUpdateDependencyGroupRequest] = useState({
		name: "",
		dependencies: []
	});
	const { t } = useTranslation();

	const axiosRef = useRef(null);

	const refreshPage = useCallback((overrideSearch = null) => {
		const axios = axiosRef.current;
		if (!axios) return;

		// an empty search string is treated the same as not specifying a search string
		const queryString = "DependencyGroup?searchString=" + (overrideSearch ?? searchString);

		axios.get(process.env.REACT_APP_SD_VALIDATOR_URL + queryString)
			.then(response => {
				setDependencyGroups(response.data);
			})
			.catch(() => {
				setDependencyGroups([]);
			});
	}, [searchString]);

	useEffect(() => {
		const httpService = HttpService;
		httpService.configure();
		axiosRef.current = httpService.getAxiosClient();

		refreshPage();
	}, [refreshPage]);

	const onSearchChangeHandler = (value) => {
		setSearchString(value);
		refreshPage(value);
	};

	const handleEditGroupSetUp = (selectedGroupName) => {
		const axios = axiosRef.current;

		axios.get(process.env.REACT_APP_SD_VALIDATOR_URL + "DependencyGroup/" + encodeURIComponent(selectedGroupName) + "?loadValues=true")
			.then(response => {
				var newGroupRequest = structuredClone(createOrUpdateDependencyGroupRequest);
				newGroupRequest.dependencies = response.data.dependencies;
				newGroupRequest.name = selectedGroupName;
				setCreateOrUpdateDependencyGroupRequest(newGroupRequest);
				setEditing(true);
				setSelectedGroupName(selectedGroupName);
				setShowCreateGroupForm(true);
			})
			.catch(ex => alert(ex.response?.data ?? ex.message));
	}

	const handleCreateGroupSetUp = () => {
		setCreateOrUpdateDependencyGroupRequest({ name: "", dependencies: [] });
		setEditing(false);
		setShowCreateGroupForm(true);
	}

	const handleCreateGroup = () => {
		const axios = axiosRef.current;
		axios.post(process.env.REACT_APP_SD_VALIDATOR_URL + "DependencyGroup", createOrUpdateDependencyGroupRequest)
			.then(_ => window.location.reload())
			.catch(ex => alert(ex.response?.data ?? ex.message));
	}

	const handleEditGroup = () => {
		const axios = axiosRef.current;
		axios.put(process.env.REACT_APP_SD_VALIDATOR_URL + "DependencyGroup/" + selectedGroupName, createOrUpdateDependencyGroupRequest)
			.then(_ => window.location.reload())
			.catch(ex => alert(ex.response?.data ?? ex.message));
	}

	const handleFilesChanged = (files) => {
		var newGroupRequest = structuredClone(createOrUpdateDependencyGroupRequest);
		var filesLoaded = 0;
		const onFileLoadFunc = (e, file) => {
			const text = e.target.result;
			newGroupRequest.dependencies[newGroupRequest.dependencies.length] = {
				name: file.name.split('.')[0],
				value: text,
				valueLoaded: true,
				schemaType: 0,
				dependencies: []
			};
			filesLoaded++;
			if (filesLoaded === files.length) setCreateOrUpdateDependencyGroupRequest(newGroupRequest);
		}

		for (let i = 0; i < files.length; i++) {
			const reader = new FileReader();

			reader.onload = (e) => onFileLoadFunc(e, files[i]);

			reader.onerror = (e) => { alert('Error reading file:', e.target.error); };
			reader.readAsText(files[i]);
		}
	}

	return (
		<div className="relative size-full">
			{showCreateGroupForm ? (
				<CreateDependencyGroupForm
					onCancel={() => setShowCreateGroupForm(false)}
					onConfirm={editing ? (handleEditGroup) : (handleCreateGroup)}
					onFilesChanged={handleFilesChanged}
					onNameChanged={(newName) => {
						var tempRequest = structuredClone(createOrUpdateDependencyGroupRequest);
						tempRequest.name = newName;
						setCreateOrUpdateDependencyGroupRequest(tempRequest);
					}}
					onDependencyRemoved={(index) => {
						var tempRequest = structuredClone(createOrUpdateDependencyGroupRequest);
						tempRequest.dependencies.splice(index, 1);
						setCreateOrUpdateDependencyGroupRequest(tempRequest);
					}}
					createGroupRequest={createOrUpdateDependencyGroupRequest}
					editing={editing}
					nameValue={createOrUpdateDependencyGroupRequest.name}
				/>
			) : ("")}
			<div className="flex flex-row size-full gap-5">
				{/* MAIN CONTENT */}
				<div className="flex flex-col items-center size-full py-2">

					{/* HEADER */}
					<div className="bg-[#00aaad] dark:bg-[#008b8e] w-full flex justify-center py-[10px] border-2 border-[#b0b6c1]">
						<div className="font-bold text-[20px] py-3">
							{t("validatorOverzicht.header")}
						</div>
					</div>

					{/* CONTENT */}
					<div className="bg-[rgba(243,244,246,0.75)] dark:bg-[rgba(55,65,81,0.75)] w-full border-2 border-[#b0b6c1]">
						<div className="flex flex-col items-center px-[28px] py-[10px] gap-[10px]">

							{/* SEARCH + BUTTON */}
							<div className="flex justify-between w-full h-[23px]">
								<div className="flex-row">
									<label className="font-bold px-[2px]">{t("validatorOverzicht.searchLabel")}:</label>
									<input
										className="dark:bg-gray-700 w-[320px] px-2 border border-black"
										onChange={e => onSearchChangeHandler(e.target.value)}
										placeholder={t("validatorOverzicht.searchPlaceholder")}
									/>
								</div>
								<Button text={t("validatorOverzicht.newBtn")} onClick={() => handleCreateGroupSetUp()} height="23" width="290" />
							</div>

							{/* TABLE */}
							<div className="flex flex-col w-full">
								<div className="bg-[rgba(58,173,255,0.5)] dark:bg-[rgba(101,191,255,0.5)] h-[35px] w-full flex items-center px-[10px] font-bold border border-black">
									<div className="flex items-center w-[97.5%]"> {/*messy bandaid solution to make the headers align despite item count mismatch in table rows due to added edit btns*/}
										<div className="flex-1">{t("validatorOverzicht.dependencyGroupNameTableheader")}</div>
										<div className="flex-1">{t("validatorOverzicht.dependantCountTableHeader")}</div>
										<div className="flex-1">{t("validatorOverzicht.dependantNamesTableHeader")}</div>
									</div>
								</div>

								{dependencyGroups.map((dg, index) => (
									<DependencyGroup key={dg.name} dependencyGroup={dg} index={index} onEditClick={() => handleEditGroupSetUp(dg.name)} />
								))}
							</div>
						</div>
					</div>

				</div>
			</div>
		</div>
	);
};

export default ValidatorOverzichtPagina;